import { Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography } from '@mui/material';
import AlertDialog from '@/common/presentation/components/AlertDialog';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import { useRef, useState } from 'react';
import { isDate, isSameDay, isEqual } from 'date-fns';
import type { FormikHelpers } from 'formik';
import { ERPJobCreateResourcesStep } from './ERPJobCreateResourcesStep';
import { ERPCreateModalDialogActions } from './ERPCreateModalDialogActions';
import { ResourceUnavailabilityWarningModal } from '@/common/presentation/components/ResourceUnavailabilityWarningModal';
import { useERPJobCreateModal, type UseERPJobCreateModalParams } from '../hook/useERPJobCreateModal';
import { ERPJobFormValues } from '@/raptorjob/presentation/ERPJobValidationSchema';
import { ERPJobInfoStep } from '../ERPJobSteps/ERPJobInfoStep';
import tid from '@/common/infrastructure/config/tid';
import { JOB_FORM_MODES, JOB_FORM_STEPS, JOB_MODAL_TITLES, JOB_MODAL_SUBTITLES } from '@/raptorjob/domain/constants/jobFormLabels';
import { isRuleEnabled, UI_BEHAVIOR_RULES } from '@/raptorjob/application/utils/erpSettings';
import { DateChangeWarningContent } from './DateChangeWarningContent';
import { TimeChangeWarningContent } from './TimeChangeWarningContent';
import {
  getDateChangeWarningDoNotShowAgain,
  setDateChangeWarningDoNotShowAgain,
  getTimeChangeWarningDoNotShowAgain,
  setTimeChangeWarningDoNotShowAgain
} from './jobWarningPreferences';

const startDayIsDifferent = (a: Date, b: Date): boolean => {
  if (isSameDay(a, b)) return false;
  return true;
};

const startTimeIsDifferent = (a: Date, b: Date): boolean => {
  if (isEqual(a, b)) return false;
  return true;
};

export const ERPJobCreateForm = (props: UseERPJobCreateModalParams) => {
  const { handleClose, mode = JOB_FORM_MODES.CREATE, jobId } = props;

  const {
    step,
    setStep,
    hasUnavailableResources,
    setHasUnavailableResources,
    showResourceUnavailabilityWarningModal,
    setShowResourceUnavailabilityWarningModal,
    jobSiteSearchTerm,
    setJobSiteSearchTerm,
    crewFields,
    setCrewFields,
    selectedEquipment,
    setSelectedEquipment,
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    selectedMaxSize,
    setSelectedMaxSize,
    strict,
    setStrict,
    equipmentSearchTerm,
    setEquipmentSearchTerm,
    initialValues,
    validationSchema,
    handleSave,
    handleCloseWithPersist,
    trackFormValues,
    editData,
    autoPopulatedDispatcher,
    availableDispatchLocations,
    requiredFields,
    uiBehaviorRules,
    jobEndDateActive,
    setJobEndDateActive,
    setFieldVisible,
    setPoRequired
  } = useERPJobCreateModal(props);

  const [showDateChangeWarningModal, setShowDateChangeWarningModal] = useState(false);
  const [showTimeChangeWarningModal, setShowTimeChangeWarningModal] = useState(false);
  const [dateChangeDoNotShowAgainChecked, setDateChangeDoNotShowAgainChecked] = useState(false);
  const [timeChangeDoNotShowAgainChecked, setTimeChangeDoNotShowAgainChecked] = useState(false);
  const pendingSaveRef = useRef<{ values: ERPJobFormValues; helpers: FormikHelpers<ERPJobFormValues> } | null>(null);
  const timeChangedRef = useRef(false);

  const removeUnitCrewEnabled = isRuleEnabled(uiBehaviorRules, UI_BEHAVIOR_RULES.REMOVE_UNIT_CREW_ON_DATE_CHANGE);
  const resetStatusEnabled = isRuleEnabled(uiBehaviorRules, UI_BEHAVIOR_RULES.RESET_STATUS_ON_DATE_CHANGE);
  const clearNotifiedAcknowledgedEnabled = isRuleEnabled(uiBehaviorRules, UI_BEHAVIOR_RULES.CLEAR_NOTIFIED_ACKNOWLEDGED_ON_DATE_CHANGE);
  const clearNotifiedOnTimeChangeEnabled = isRuleEnabled(uiBehaviorRules, UI_BEHAVIOR_RULES.CLEAR_NOTIFIED_ON_TIME_CHANGE);

  const handleProceedDateChange = async () => {
    if (dateChangeDoNotShowAgainChecked) setDateChangeWarningDoNotShowAgain(true);
    setDateChangeDoNotShowAgainChecked(false);
    setShowDateChangeWarningModal(false);
    const pending = pendingSaveRef.current;
    if (!pending) return;

    const shouldShowTimeWarning = timeChangedRef.current && clearNotifiedOnTimeChangeEnabled && !getTimeChangeWarningDoNotShowAgain();
    if (shouldShowTimeWarning) {
      setShowTimeChangeWarningModal(true);
      return;
    }

    await handleSave(pending.values, pending.helpers);
  };

  const handleProceedTimeChange = async () => {
    if (timeChangeDoNotShowAgainChecked) setTimeChangeWarningDoNotShowAgain(true);
    setTimeChangeDoNotShowAgainChecked(false);
    setShowTimeChangeWarningModal(false);
    const pending = pendingSaveRef.current;
    if (!pending) return;
    await handleSave(pending.values, pending.helpers);
  };

  return (
    <Dialog
      open
      onClose={handleCloseWithPersist}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            maxWidth: {
              xs: '360px',
              sm: '620px',
              md: '768px'
            }
          }
        }
      }}
    >
      <Formik<ERPJobFormValues>
        key={mode === JOB_FORM_MODES.EDIT && jobId ? `edit-job-${jobId}` : 'create-job'}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSave}
        validateOnChange
        validateOnBlur
      >
        {(helpers) => {
          trackFormValues(helpers.values);

          const isEditMode = mode === JOB_FORM_MODES.EDIT;

          // Treat as changed when user has a date and: initial was missing, or initial was invalid, or the day is different
          const hasValidCurrentDate = isDate(helpers.values.startDate);
          const hasValidInitialDate = isDate(initialValues.startDate);
          const currentStartDate = helpers.values.startDate;
          const initialStartDate = initialValues.startDate;
          const startDateHasChanged =
            hasValidCurrentDate &&
            (!hasValidInitialDate ||
              (currentStartDate != null && initialStartDate != null && startDayIsDifferent(currentStartDate, initialStartDate)));

          const startTimeHasChanged =
            isDate(helpers.values.startTime) &&
            isDate(initialValues.startTime) &&
            startTimeIsDifferent(helpers.values.startTime, initialValues.startTime);

          const dateChanged = isEditMode && startDateHasChanged;
          const timeChanged = isEditMode && startTimeHasChanged;

          timeChangedRef.current = timeChanged;

          // Show date warning whenever the date changed in edit mode (not only when a rule is enabled)
          const shouldShowDateWarning = dateChanged;
          const shouldShowTimeWarning = timeChanged && clearNotifiedOnTimeChangeEnabled;

          return (
            <>
              <Form noValidate>
                <DialogTitle>
                  {mode === JOB_FORM_MODES.EDIT ? JOB_MODAL_TITLES.EDIT_JOB : JOB_MODAL_TITLES.NEW_JOB}
                  <Typography variant="caption" component="p">
                    {mode === JOB_FORM_MODES.EDIT && JOB_MODAL_SUBTITLES.UPDATE_JOB_DETAILS}
                    {mode === JOB_FORM_MODES.CREATE && step === JOB_FORM_STEPS.JOB_INFO && JOB_MODAL_SUBTITLES.COMPLETE_DETAILS}
                    {mode === JOB_FORM_MODES.CREATE && step === JOB_FORM_STEPS.RESOURCES && JOB_MODAL_SUBTITLES.ADD_RESOURCES}
                  </Typography>
                </DialogTitle>
                <IconButton
                  aria-label="close"
                  onClick={handleCloseWithPersist}
                  sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500]
                  })}
                  {...tid('jobs:create:modal:close-button')}
                >
                  <CloseIcon />
                </IconButton>
                <Divider />
                <DialogContent sx={{ padding: { xs: 2, sm: '20px 24px' } }}>
                  {step === JOB_FORM_STEPS.JOB_INFO && (
                    <ERPJobInfoStep
                      jobSiteSearchTerm={jobSiteSearchTerm}
                      setJobSiteSearchTerm={setJobSiteSearchTerm}
                      mode={mode}
                      availableDispatchLocations={availableDispatchLocations}
                      rawCaller={editData?.caller}
                      callerInfo={editData?.callerInfo}
                      vendorId={editData?.vendorId}
                      vendor={editData?.vendor}
                      dispatcher={editData?.dispatcher ?? autoPopulatedDispatcher}
                      dispatchLocation={editData?.dispatchLocation}
                      salesAgentId={editData?.salesAgentId}
                      salesAgent={editData?.salesAgent}
                      jobType={
                        editData
                          ? props.jobData?.type
                            ? { ...props.jobData.type, id: Number(props.jobData.type.id) }
                            : undefined
                          : undefined
                      }
                      jobSite={
                        editData
                          ? {
                              id: Number(props.jobData?.jobSite?.id ?? 0),
                              name: props.jobData?.jobSite?.name ?? 'Unknown JobSite',
                              address: props.jobData?.jobSite?.address
                            }
                          : undefined
                      }
                      flag1={editData?.flag1}
                      flag2={editData?.flag2}
                      flag3={editData?.flag3}
                      requiredFields={requiredFields}
                      jobEndDateActive={jobEndDateActive}
                      setJobEndDateActive={setJobEndDateActive}
                      setFieldVisible={setFieldVisible}
                      onPORequiredChange={setPoRequired}
                    />
                  )}
                  {mode === JOB_FORM_MODES.CREATE && step === JOB_FORM_STEPS.RESOURCES && (
                    <ERPJobCreateResourcesStep
                      onUnavailableResources={setHasUnavailableResources}
                      crewFields={crewFields}
                      setCrewFields={setCrewFields}
                      selectedEquipment={selectedEquipment}
                      setSelectedEquipment={setSelectedEquipment}
                      selectedClass={selectedClass}
                      setSelectedClass={setSelectedClass}
                      selectedType={selectedType}
                      setSelectedType={setSelectedType}
                      selectedSize={selectedSize}
                      setSelectedSize={setSelectedSize}
                      selectedMaxSize={selectedMaxSize}
                      setSelectedMaxSize={setSelectedMaxSize}
                      strict={strict}
                      setStrict={setStrict}
                      equipmentSearchTerm={equipmentSearchTerm}
                      setEquipmentSearchTerm={setEquipmentSearchTerm}
                    />
                  )}
                </DialogContent>
                <ERPCreateModalDialogActions
                  onSubmit={async () => {
                    if (mode === JOB_FORM_MODES.CREATE && hasUnavailableResources) {
                      setShowResourceUnavailabilityWarningModal(true);
                      return;
                    }

                    if (shouldShowDateWarning && !getDateChangeWarningDoNotShowAgain()) {
                      pendingSaveRef.current = { values: helpers.values, helpers };
                      setShowDateChangeWarningModal(true);
                      return;
                    }

                    if (shouldShowTimeWarning && !getTimeChangeWarningDoNotShowAgain()) {
                      pendingSaveRef.current = { values: helpers.values, helpers };
                      setShowTimeChangeWarningModal(true);
                      return;
                    }

                    await handleSave(helpers.values, helpers);
                  }}
                  step={mode === JOB_FORM_MODES.EDIT ? JOB_FORM_STEPS.JOB_INFO : step}
                  setStep={mode === JOB_FORM_MODES.EDIT ? () => {} : setStep}
                  handleClose={handleClose}
                  mode={mode}
                  crewFields={crewFields}
                  selectedSize={selectedSize}
                  jobEndDateActive={jobEndDateActive}
                />
              </Form>

              {mode === JOB_FORM_MODES.CREATE && showResourceUnavailabilityWarningModal && (
                <ResourceUnavailabilityWarningModal
                  open={showResourceUnavailabilityWarningModal}
                  onClose={() => setShowResourceUnavailabilityWarningModal(false)}
                  onConfirm={async () => {
                    await handleSave(helpers.values, helpers);
                    setShowResourceUnavailabilityWarningModal(false);
                    setHasUnavailableResources(false);
                  }}
                  confirmText="Create Job & Job Ticket"
                  cancelText="Cancel"
                />
              )}

              <AlertDialog
                open={showDateChangeWarningModal}
                onClose={() => {
                  setShowDateChangeWarningModal(false);
                  setDateChangeDoNotShowAgainChecked(false);
                }}
                title="Update Job Date"
                subtitle="Changing the date has side effects"
                content={
                  <DateChangeWarningContent
                    removeUnitCrewEnabled={removeUnitCrewEnabled}
                    resetStatusEnabled={resetStatusEnabled}
                    clearNotifiedAcknowledgedEnabled={clearNotifiedAcknowledgedEnabled}
                  />
                }
                buttonText="PROCEED"
                onConfirm={handleProceedDateChange}
                onConfirmButtonType="button"
                actionLoading={helpers.isSubmitting}
                showDoNotShowAgain
                doNotShowAgainChecked={dateChangeDoNotShowAgainChecked}
                onDoNotShowAgainChange={setDateChangeDoNotShowAgainChecked}
              />

              <AlertDialog
                open={showTimeChangeWarningModal}
                onClose={() => {
                  setShowTimeChangeWarningModal(false);
                  setTimeChangeDoNotShowAgainChecked(false);
                }}
                title="Update Job Time"
                subtitle="Changing the time has side effects"
                content={<TimeChangeWarningContent clearNotifiedOnTimeChangeEnabled={clearNotifiedOnTimeChangeEnabled} />}
                buttonText="PROCEED"
                onConfirm={handleProceedTimeChange}
                onConfirmButtonType="button"
                actionLoading={helpers.isSubmitting}
                showDoNotShowAgain
                doNotShowAgainChecked={timeChangeDoNotShowAgainChecked}
                onDoNotShowAgainChange={setTimeChangeDoNotShowAgainChecked}
              />
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};
